import { CoreFileItem } from "@verity/components/core";
import { DeltaStatic } from "quill";
import ReactQuill from "react-quill";
import { useState, useCallback, useEffect } from "react";
import i18n from "../../../../../i18n";
import { baseTransKey, mapQuizQuestionToAssignment, MAX_FILE_UPLOAD } from "../verityAssessmentHelper";
import { QuizQuestionInput } from "@verity/api-client/types";

const Quill = ReactQuill.Quill;

function getInstructionDefaults(): AddInstructionsInitialValues {
	return {
		instructions: i18n.t(`${baseTransKey}.addInstructions.placeholder`) as string,
		uploadedFiles: [],
		instructionDelta: JSON.parse(
			'{"ops":[{"insert":"Input instructions"},{"attributes":{"header":4},"insert":"\\n"}]}',
		) as DeltaStatic,
	};
}

export function buildAddInstructionsInitialValues(
	uploadedFiles?: AddInstructionsInitialValues["uploadedFiles"],
	quizQuestions?: QuizQuestionInput[],
): AddInstructionsInitialValues {
	const defaults = getInstructionDefaults();
	const assignmentInfo = mapQuizQuestionToAssignment(quizQuestions);

	const instructions = assignmentInfo?.instructions ?? defaults.instructions;

	const tempCont = document.createElement("div");
	const tempQuill = new Quill(tempCont);
	const parsedDelta = tempQuill.clipboard.convert(instructions);

	return {
		instructions,
		instructionDelta: parsedDelta,
		uploadedFiles: uploadedFiles ?? defaults.uploadedFiles,
	};
}

type AddInstructionsInitialValues = {
	instructions: string;
	instructionDelta: DeltaStatic;
	uploadedFiles: CoreFileItem[];
};

export default function useAddInstructions(initialValues: AddInstructionsInitialValues) {
	const [instructions, setInstructions] = useState<string>(initialValues.instructions);
	const [instructionDelta, setInstructionDelta] = useState<DeltaStatic>(initialValues.instructionDelta);
	const [uploadedFiles, setUploadedFiles] = useState<CoreFileItem[]>(initialValues.uploadedFiles);

	useEffect(() => {
		setInstructions(initialValues.instructions);
		setInstructionDelta(initialValues.instructionDelta);
		setUploadedFiles(initialValues.uploadedFiles);
	}, [initialValues]);

	const updateInstructions = useCallback((text: string, delta: DeltaStatic) => {
		setInstructions(text);
		setInstructionDelta(delta);
	}, []);

	const addUploadedFile = useCallback((files: File[]) => {
		setUploadedFiles((prev) => {
			const remainingSlots = MAX_FILE_UPLOAD - prev.length;

			if (remainingSlots <= 0) {
				return prev;
			}

			const slicedFiles = files.slice(0, remainingSlots);

			const newFiles: CoreFileItem[] = slicedFiles.map((file) => ({
				name: file.name,
				size: file.size,
				type: file.type,
				nativeFile: file,
			}));
			return [...prev, ...newFiles];
		});
	}, []);

	const removeUploadedFile = useCallback((fileName: string) => {
		setUploadedFiles((prev) => prev.filter((file) => file.name !== fileName));
	}, []);

	return {
		instructions,
		instructionDelta,
		uploadedFiles,
		onInstructionsChange: updateInstructions,
		onAddFile: addUploadedFile,
		onRemoveFile: removeUploadedFile,
	};
}

export type AddInstructionsReturn = ReturnType<typeof useAddInstructions>;
